"use client";

import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMediaAssetGet } from "@/features/media/hooks";
import { useTenantBranding } from "@/lib/contexts/TenantBrandingContext";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";
import { useTenantData } from "./useTenantData";

/**
 * Resolves tenant logo URL the same way as DashboardSidebar:
 * live media query → React Query cache → server-prefetched initial URL.
 */
export function useTenantLogoUrl() {
  const { tenantId } = useTenantData();
  const { initialLogoUrl } = useTenantBranding();
  const queryClient = useQueryClient();

  const mediaAssetParams = useMemo(
    () => ({
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    }),
    [tenantId],
  );

  const { url: logoUrl } = useMediaAssetGet(mediaAssetParams, {
    enabled: !!tenantId && tenantId > 0,
  });

  const cachedUrl = queryClient.getQueryData<{ url: string | null }>(
    getMediaAssetQueryKey(
      tenantId,
      EntryType.Tenant,
      tenantId,
      MediaGroup.Logo,
      "auth",
    ),
  )?.url;

  return logoUrl ?? cachedUrl ?? initialLogoUrl ?? null;
}
